import type { SubmissionErrors } from '@ff-it/form';
import invariant from 'tiny-invariant';
import type { FetchResult } from 'services';
import { PlanDiffResponse } from '../diff';
import { PlanStore } from './plan';

type DiffKey = keyof PlanDiffResponse;
type UpsertItem<K extends DiffKey> = NonNullable<NonNullable<PlanDiffResponse[K]>['upsert']>[number];

export function getCreatedItems<K extends DiffKey>(
  key: K,
  response: PlanDiffResponse,
  prevState: PlanStore,
): UpsertItem<K>[] {
  const upserted = response[key]?.upsert ?? [];
  const existingIds = new Set(Object.keys(prevState[key] || {}).map(Number));

  return upserted.filter((item) => !existingIds.has(item.id));
}

export function getSingleCreatedItem<K extends DiffKey>(
  key: K,
  response: PlanDiffResponse,
  prevState: PlanStore,
): UpsertItem<K> {
  const created = getCreatedItems(key, response, prevState);
  invariant(created.length === 1, `Expected exactly 1 new item in ${key}, but got ${created.length}`);
  return created[0];
}

/**
 * Map a bulk `plan/update` validation error back onto a single collection item's form.
 *
 * The whole list is validated server-side, so a bad field on the edited item comes back
 * keyed by its list index (e.g. `{ "2": { size: [...] } }`), which a single-item form can't
 * bind. Narrow the error to the edited item's index. Collection-level errors (uniqueness,
 * surfaced as `non_field_errors` -> `FORM_ERROR`) carry no index and fall through unchanged.
 */
export function pickItemFormError(res: FetchResult<unknown>, index: number): SubmissionErrors | void {
  const err = res.unwrapFormError();
  if (!err) {
    return;
  }
  return (err[index] as SubmissionErrors) ?? err;
}
